import { Button } from '@/components/ui/button';
import { useTimer } from '@/contexts/timer-context';
import { cn } from '@/lib/utils';
import { Maximize2, Minimize2, Pause, Play, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

// Local storage key for timer position
const TIMER_POSITION_KEY = 'cognito_timer_position';

// Simple draggable timer component with native browser drag functionality
export const FloatingTimer = () => {
  const { projectId } = useParams();
  const {
    isTimerRunning,
    sessionSeconds,
    sessionIntentionallyEnded,
    isTimerVisible,
    pauseSession,
    resumeSession,
    hideTimer,
    formatTime
  } = useTimer();

  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // References for drag functionality
  const dragRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const currentPosRef = useRef({ x: 0, y: 0 });

  // Define event handlers outside of the render function
  const handleMouseMove = useRef((e: MouseEvent) => {
    if (!isDraggingRef.current) return;

    // Calculate new position
    const deltaX = e.clientX - startPosRef.current.x;
    const deltaY = e.clientY - startPosRef.current.y;

    const newPosition = {
      x: currentPosRef.current.x + deltaX,
      y: currentPosRef.current.y + deltaY
    };

    // Update position directly in the DOM for smoother dragging
    if (dragRef.current) {
      dragRef.current.style.left = `${newPosition.x}px`;
      dragRef.current.style.top = `${newPosition.y}px`;

      // Update visual feedback
      dragRef.current.style.opacity = '0.8';
      dragRef.current.style.cursor = 'grabbing';
    }
  });

  const handleMouseUp = useRef((e: MouseEvent) => {
    if (!isDraggingRef.current) return;

    // Calculate final position
    const deltaX = e.clientX - startPosRef.current.x;
    const deltaY = e.clientY - startPosRef.current.y;

    const finalPosition = {
      x: currentPosRef.current.x + deltaX,
      y: currentPosRef.current.y + deltaY
    };

    // Update position state
    setPosition(finalPosition);

    // End dragging
    isDraggingRef.current = false;

    // Reset visual feedback
    if (dragRef.current) {
      dragRef.current.style.opacity = '1';
      dragRef.current.style.cursor = 'auto';
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleMouseMove.current);
    document.removeEventListener('mouseup', handleMouseUp.current);

    // Save position to localStorage
    try {
      localStorage.setItem(TIMER_POSITION_KEY, JSON.stringify(finalPosition));
    } catch (e) {
      console.error('Error saving timer position:', e);
    }
  });

  const handleTouchMove = useRef((e: TouchEvent) => {
    if (!isDraggingRef.current) return;

    // Prevent scrolling
    e.preventDefault();

    // Calculate new position
    const touch = e.touches[0];
    const deltaX = touch.clientX - startPosRef.current.x;
    const deltaY = touch.clientY - startPosRef.current.y;

    const newPosition = {
      x: currentPosRef.current.x + deltaX,
      y: currentPosRef.current.y + deltaY
    };

    // Update position directly in the DOM for smoother dragging
    if (dragRef.current) {
      dragRef.current.style.left = `${newPosition.x}px`;
      dragRef.current.style.top = `${newPosition.y}px`;

      // Update visual feedback
      dragRef.current.style.opacity = '0.8';
    }
  });

  const handleTouchEnd = useRef((e: TouchEvent) => {
    if (!isDraggingRef.current) return;

    // Get the current position from the DOM element
    let finalPosition = { ...position };
    if (dragRef.current) {
      const left = parseInt(dragRef.current.style.left, 10) || position.x;
      const top = parseInt(dragRef.current.style.top, 10) || position.y;
      finalPosition = { x: left, y: top };

      // Reset visual feedback
      dragRef.current.style.opacity = '1';
    }

    // Update position state
    setPosition(finalPosition);

    // End dragging
    isDraggingRef.current = false;

    // Remove event listeners
    document.removeEventListener('touchmove', handleTouchMove.current, { passive: false } as EventListenerOptions);
    document.removeEventListener('touchend', handleTouchEnd.current);

    // Save position to localStorage
    try {
      localStorage.setItem(TIMER_POSITION_KEY, JSON.stringify(finalPosition));
    } catch (e) {
      console.error('Error saving timer position:', e);
    }
  });

  // Mouse event handler for starting drag
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left mouse button

    // Prevent text selection during drag
    e.preventDefault();

    // Get initial mouse position
    startPosRef.current = {
      x: e.clientX,
      y: e.clientY
    };

    // Store current position
    currentPosRef.current = { ...position };

    // Start dragging
    isDraggingRef.current = true;

    // Add event listeners for drag and release
    document.addEventListener('mousemove', handleMouseMove.current);
    document.addEventListener('mouseup', handleMouseUp.current);
  };

  // Touch event handler for starting drag
  const handleTouchStart = (e: React.TouchEvent) => {
    // Prevent scrolling
    e.preventDefault();

    // Get initial touch position
    const touch = e.touches[0];
    startPosRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };

    // Store current position
    currentPosRef.current = { ...position };

    // Start dragging
    isDraggingRef.current = true;

    // Add event listeners for drag and release
    document.addEventListener('touchmove', handleTouchMove.current, { passive: false });
    document.addEventListener('touchend', handleTouchEnd.current);
  };

  // Load saved position from localStorage on mount and set up cleanup
  useEffect(() => {
    // Load position
    try {
      const savedPosition = localStorage.getItem(TIMER_POSITION_KEY);
      if (savedPosition) {
        setPosition(JSON.parse(savedPosition));
      } else {
        // Default position (bottom right)
        setPosition({ x: window.innerWidth - 300, y: window.innerHeight - 200 });
      }
    } catch (e) {
      console.error('Error loading timer position:', e);
      // Default position if there's an error
      setPosition({ x: window.innerWidth - 300, y: window.innerHeight - 200 });
    }

    // Cleanup function to remove any lingering event listeners
    return () => {
      document.removeEventListener('mousemove', handleMouseMove.current);
      document.removeEventListener('mouseup', handleMouseUp.current);
      document.removeEventListener('touchmove', handleTouchMove.current, { passive: false } as EventListenerOptions);
      document.removeEventListener('touchend', handleTouchEnd.current);
    };
  }, []);

  // Don't render if timer is not visible or there's no active session
  // We need to check this condition before rendering
  useEffect(() => {
    console.log('Timer visibility changed:', isTimerVisible);
  }, [isTimerVisible]);

  if (!isTimerVisible || (sessionSeconds === 0 && !sessionIntentionallyEnded)) {
    console.log('Not rendering timer: visible=', isTimerVisible, 'seconds=', sessionSeconds);
    return null;
  }

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // End session functionality removed from the floating timer

  const handleCloseTimer = () => {
    console.log('Close timer button clicked');
    hideTimer();
    console.log('Timer visibility after hideTimer:', isTimerVisible);
  };

  return (
    <div
      ref={dragRef}
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        zIndex: 50,
        transition: 'opacity 0.2s',
      }}
      className={cn(
        "rounded-2xl border border-border/50 bg-background/40 backdrop-blur-xl shadow-2xl transition-all duration-300 hover:bg-background/50 group",
        isMinimized ? "w-auto" : "w-[280px]"
      )}
    >
      {isMinimized ? (
        // Minimized view
        <div className="p-2 flex items-center gap-3">
          <div
            className="flex items-center gap-3 cursor-grab active:cursor-grabbing px-2 py-1"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div className={`relative flex h-2 w-2 rounded-full ${isTimerRunning ? 'bg-green-500' : 'bg-orange-500'}`}>
              {isTimerRunning && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>}
            </div>
            <div className="font-mono font-bold tracking-tighter text-lg tabular-nums text-foreground/90">{formatTime(sessionSeconds)}</div>
          </div>

          <div className="h-4 w-px bg-border/50" />

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-background/60 hover:text-foreground"
              onClick={toggleMinimize}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive"
              onClick={handleCloseTimer}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        // Full view
        <div className="relative overflow-hidden rounded-2xl">
          {/* Background Decor */}
          <div className="absolute top-0 right-0 p-16 bg-primary/5 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" />

          {/* Header / Drag Handle */}
          <div
            className="flex items-center justify-between p-4 pb-0 cursor-grab active:cursor-grabbing group-hover:opacity-100 transition-opacity"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div className="flex items-center gap-2">
              <div className={`relative flex h-2 w-2 rounded-full ${isTimerRunning ? 'bg-green-500' : 'bg-orange-500'}`}>
                {isTimerRunning && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>}
              </div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Session</span>
            </div>

            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-md hover:bg-background/60 text-muted-foreground"
                onClick={toggleMinimize}
                title="Minimize"
              >
                <Minimize2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                onClick={handleCloseTimer}
                title="Close Widget"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          <div className="p-6 pt-2 flex flex-col items-center">
            {/* Timer Display */}
            <div className="mb-6 mt-1 text-center">
              <div className={`text-5xl font-mono font-bold tracking-tighter tabular-nums leading-none transition-colors duration-300 ${isTimerRunning ? 'text-foreground' : 'text-muted-foreground'}`}>
                {formatTime(sessionSeconds)}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center p-1.5 bg-muted/50 backdrop-blur-md rounded-xl border border-border/50 shadow-sm w-full justify-between">
              {isTimerRunning ? (
                <Button
                  onClick={pauseSession}
                  variant="ghost"
                  size="sm"
                  className="flex-1 h-9 rounded-lg hover:bg-background hover:text-orange-500 transition-all"
                  title="Pause Session"
                >
                  <Pause className="h-4 w-4 fill-current" />
                </Button>
              ) : (
                <Button
                  onClick={resumeSession}
                  variant="ghost"
                  size="sm"
                  className="flex-1 h-9 rounded-lg hover:bg-background hover:text-green-500 transition-all"
                  title="Resume Session"
                >
                  <Play className="h-4 w-4 fill-current" />
                </Button>
              )}
            </div>

            <p className="text-[10px] text-muted-foreground/60 text-center mt-3 font-medium">
              Drag to move • Double click dashboard to open
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
